import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader';

export function AppleLogo({ opacity = 1, scale = 1, glowIntensity = 0.5 }) {
  const groupRef = useRef();
  const glowRef = useRef();

  // Apple logo SVG path (based on Font Awesome apple icon - accurate representation)
  const appleShapes = useMemo(() => {
    const svgPath = `M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z`;

    const loader = new SVGLoader();
    const paths = loader.parse(`<svg viewBox="0 0 384 512"><path d="${svgPath}"/></svg>`).paths;

    const shapes = [];
    paths.forEach((path) => {
      const pathShapes = SVGLoader.createShapes(path);
      shapes.push(...pathShapes);
    });

    return shapes;
  }, []);

  useFrame((state) => {
    if (glowRef.current) {
      const pulse = Math.sin(state.clock.elapsedTime * 1.5) * 0.1 + 0.9;
      glowRef.current.material.opacity = glowIntensity * pulse * opacity * 0.5;
    }
  });

  // Create radial gradient texture for soft glow
  const glowTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.15, 'rgba(255, 255, 255, 0.6)');
    gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.2)');
    gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.05)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);

    const texture = new THREE.CanvasTexture(canvas);
    return texture;
  }, []);

  // Scale and center the logo
  const logoScale = 0.00134946;
  const offsetX = -192 * logoScale; // Center horizontally (384/2)
  const offsetY = 370 * logoScale;  // Moved up 100 points

  // Additional offset to move logo up (approximately 215px)
  const verticalOffset = 0.4;

  return (
    <group ref={groupRef} scale={scale} position={[0, verticalOffset, 0]}>
      {/* Soft radial glow behind the logo */}
      <mesh ref={glowRef} position={[0, 0, -0.01]}>
        <planeGeometry args={[1.5, 1.5]} />
        <meshBasicMaterial
          map={glowTexture}
          transparent
          opacity={glowIntensity * opacity * 0.5}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Apple logo shapes */}
      <group position={[offsetX, offsetY, 0]} scale={[logoScale, -logoScale, logoScale]}>
        {appleShapes.map((shape, index) => (
          <mesh key={index}>
            <shapeGeometry args={[shape]} />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={opacity}
              side={THREE.DoubleSide}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}
